import { FfsEligibility } from './computeFfsEligibility'
import { FfsLevel } from './ffsTypes'

export interface FfsLevelAvailability {
  disabled: boolean
  /** Why the level is disabled -- surfaced as the locked tab's tooltip. */
  lockReason?: string
}

export interface ComputeFfsLevelAvailabilityArgs {
  /** True only for the part-scoped Libra steps; false disables all locking. */
  isPinned: boolean
  eligibility: FfsEligibility
  /** Whether this LTA already has a saved Level 1 report for the active part. */
  level1Done: boolean
}

/**
 * Pure policy for whether each FFS level is available on the (per-LTA) calc
 * card. The rules:
 *  - Not pinned (FDE details page) -> every level enabled.
 *  - Level 1 -> disabled when the component is ineligible for it.
 *  - Level 2 -> disabled when ineligible, OR (when Level 1 is eligible) until
 *    Level 1 has a saved report. When Level 1 is ineligible, Level 2 is not
 *    gated on it, so the card auto-selects Level 2.
 *
 * Extracted from `FfsCalcCard` so the locking rules are unit-testable and so the
 * future FFS pre-planning step has one place to extend them.
 *
 * NOTE: the ineligibility branches (and their tests) are intentionally dormant
 * today -- `computeFfsEligibility` is stubbed all-true, so nothing produces an
 * ineligible level yet. They implement the "lock Level 1 if ineligible"
 * requirement and go live unchanged once the pre-planning step computes real
 * eligibility.
 */
export function computeFfsLevelAvailability({
  isPinned,
  eligibility,
  level1Done,
}: ComputeFfsLevelAvailabilityArgs): Record<FfsLevel, FfsLevelAvailability> {
  if (!isPinned) {
    return { '1': { disabled: false }, '2': { disabled: false } }
  }

  const level1: FfsLevelAvailability = eligibility.level1Eligible
    ? { disabled: false }
    : {
        disabled: true,
        lockReason:
          eligibility.reasons?.level1 ??
          'Level 1 is not applicable for this component.',
      }

  let level2: FfsLevelAvailability
  if (!eligibility.level2Eligible) {
    level2 = {
      disabled: true,
      lockReason:
        eligibility.reasons?.level2 ??
        'Level 2 is not applicable for this component.',
    }
  } else if (eligibility.level1Eligible && !level1Done) {
    level2 = { disabled: true, lockReason: 'Complete Level 1 first.' }
  } else {
    level2 = { disabled: false }
  }

  return { '1': level1, '2': level2 }
}
